import React, { useEffect, useState, useRef } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { get, post } from "../../services/api";
import Select from "react-select";
import { toast } from "react-toastify";

function InstallmentPlan() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const savedCenterId =
    localStorage.getItem("centerId") === "null"
      ? null
      : localStorage.getItem("centerId");

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    getValues,
    reset,
  } = useForm({
    defaultValues: {
      interval: 4,
    },
  });

  const [centerId, setCenterId] = useState(savedCenterId || null);
  const [sessionData, setSessionData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [monthRanges, setMonthRanges] = useState([]);

  const watchSessionId = watch("sessionId");
  const watchInterval = watch("interval");

  const intervals = [
    { id: 1, name: "1" },
    { id: 2, name: "2" },
    { id: 3, name: "3" },
    { id: 4, name: "4" },
    { id: 5, name: "5" },
    { id: 6, name: "6" },
    { id: 7, name: "7" },
    { id: 8, name: "8" },
    { id: 9, name: "9" },
    { id: 10, name: "10" },
    { id: 11, name: "11" },
    { id: 12, name: "12" },
  ];

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Fetch sessions on component mount
  useEffect(() => {
    const fetchSession = async () => {
      try {
        const response = await get(
          `Session/GetSessions?organizationId=${organizationId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setSessionData(response.sessions);
      } catch (error) {
        console.error("Error fetching sessions:", error);
      }
    };
    fetchSession();
  }, []);

  // Generate month ranges when session or interval changes
  useEffect(() => {
    if (!watchSessionId) {
      setMonthRanges([]);
      return;
    }

    const generateMonthRanges = () => {
      const ranges = [];
      const currentYear = new Date().getFullYear();

      for (let i = 0; i < 12; i++) {
        const monthIndex = i;
        const month = months[monthIndex];
        const sessionYear = currentYear;
        const sessionEndYear = sessionYear + 1;

        // Calculate which installment group this month belongs to
        const installmentGroup = watchInterval
          ? Math.floor(i / parseInt(watchInterval))
          : i;

        ranges.push({
          id: i + 1,
          month: month,
          session: `${sessionYear}-${sessionEndYear}`,
          installmentGroup: installmentGroup,
          installmentName: `Installment ${installmentGroup + 1}`,
          installmentStartDate: "",
          installmentEndDate: "",
        });
      }

      return ranges;
    };

    setMonthRanges(generateMonthRanges());
  }, [watchSessionId, watchInterval]);

  const saveData = async (data) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    // Prepare data for submission - group by installmentGroup
    const installments = monthRanges.map((monthRange, index) => {
      const groupKey = monthRange.installmentGroup;

      return {
        Month: monthRange.month, // Note: Capital 'M' to match C# property
        SessionId: data.sessionId?.value,
        SessionName: monthRange.session,
        Year: new Date().getFullYear(), // You may need to adjust this based on your logic
        InstallmentId: groupKey + 1,
        InstallmentName:
          data[`installmentName_${groupKey}`] || `Installment ${groupKey + 1}`,
        InstallmentGenerationFromDate: data[`startDate_${groupKey}`]
          ? new Date(data[`startDate_${groupKey}`]).toISOString()
          : null,
        InstallmentGenerationToDate: data[`endDate_${groupKey}`]
          ? new Date(data[`endDate_${groupKey}`]).toISOString()
          : null,
      };
    });

    try {
      const response = await post("Session/SaveInstallmentPlan", installments, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (response.isSuccess) {
        toast.success(
          response.message || "Installment plan saved successfully!"
        );
        reset();
        setMonthRanges([]);
      } else {
        toast.error(response.message || "Failed to save installment plan");
      }
    } catch (error) {
      console.error("Error saving installment plan:", error);
      toast.error("An error occurred while saving the installment plan");
    }
  };

  // Function to check if current row should show input fields
  const shouldShowInputs = (index) => {
    if (!watchInterval) return true;
    const interval = parseInt(watchInterval);
    return index % interval === 0;
  };

  // Function to get rowspan for merged cells
  const getRowspan = () => {
    if (!watchInterval) return 1;
    return parseInt(watchInterval);
  };

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Installment Plan</h2>
      </div>

      <div className="row g-3">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
              <form
                className="new-form-style"
                onSubmit={handleSubmit(saveData)}
              >
                <div className="row g-3">
                  <div className="col-12" style={{ width: "220px" }}>
                    <label className="form-label form-label-new">Session</label>
                    <Controller
                      name="sessionId"
                      control={control}
                      rules={{ required: "This field is required." }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={sessionData.map((session) => ({
                            value: session.id,
                            label: session.name,
                          }))}
                          onChange={(selectedOption) => {
                            field.onChange(selectedOption);
                            setMonthRanges([]);
                          }}
                          className="react-select-style"
                          classNamePrefix="react-select-style-pref"
                        />
                      )}
                    />
                    {errors.sessionId && (
                      <small className="text-danger">
                        {errors.sessionId.message}
                      </small>
                    )}
                  </div>

                  <div className="col-12" style={{ width: "220px" }}>
                    <label className="form-label form-label-new">
                      Interval<span className="text-danger fs-9">*</span>
                    </label>
                    <Controller
                      name="interval"
                      control={control}
                      rules={{ required: "This field is required." }}
                      render={({ field }) => (
                        <select
                          {...field}
                          className="form-select"
                          value={field.value || ""}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        >
                          <option value="" disabled>
                            Select Interval
                          </option>
                          {intervals.map((interval) => (
                            <option key={interval.id} value={interval.id}>
                              {interval.name}
                            </option>
                          ))}
                        </select>
                      )}
                    />
                    {errors.interval && (
                      <small className="text-danger">
                        {errors.interval.message}
                      </small>
                    )}
                  </div>
                </div>

                {/* Month Ranges Table */}
                <div className="row g-3 mt-4">
                  <div className="col-md-12">
                    {monthRanges.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-bordered">
                          <thead className="table-light">
                            <tr>
                              <th>Month</th>
                              <th>Session</th>
                              <th>Installment Name</th>
                              <th>Installment Start Date</th>
                              <th>Installment End Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {monthRanges.map((monthRange, index) => (
                              <tr key={monthRange.id}>
                                <td>{monthRange.month}</td>
                                <td>{monthRange.session}</td>

                                {/* Installment Name - Merged based on interval */}
                                {shouldShowInputs(index) ? (
                                  <td rowSpan={getRowspan()}>
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      placeholder="Input Box For Name"
                                      {...register(
                                        `installmentName_${monthRange.installmentGroup}`
                                      )}
                                      defaultValue={monthRange.installmentName}
                                    />
                                  </td>
                                ) : null}

                                {/* Installment Start Date - Merged based on interval */}
                                {shouldShowInputs(index) ? (
                                  <td rowSpan={getRowspan()}>
                                    <input
                                      type="date"
                                      className="form-control form-control-sm"
                                      {...register(
                                        `startDate_${monthRange.installmentGroup}`
                                      )}
                                      defaultValue={
                                        monthRange.installmentStartDate
                                      }
                                    />
                                  </td>
                                ) : null}

                                {/* Installment End Date - Merged based on interval */}
                                {shouldShowInputs(index) ? (
                                  <td rowSpan={getRowspan()}>
                                    <input
                                      type="date"
                                      className="form-control form-control-sm"
                                      {...register(
                                        `endDate_${monthRange.installmentGroup}`
                                      )}
                                      defaultValue={
                                        monthRange.installmentEndDate
                                      }
                                    />
                                  </td>
                                ) : null}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {watchInterval && watchInterval > 1 && (
                          <div className="alert alert-info mt-2">
                            <small>
                              <strong>Note:</strong> Input boxes are merged in
                              groups of {watchInterval} months. Each input box
                              covers {watchInterval} month(s).
                            </small>
                          </div>
                        )}
                      </div>
                    ) : watchSessionId ? (
                      <div className="alert alert-info">
                        Select session to generate month ranges.
                      </div>
                    ) : (
                      <div className="alert alert-warning d-none">
                        Please select a session to view the table.
                      </div>
                    )}
                  </div>

                  <div className="col-12">
                    <button
                      className="btn btn-primary btn-sm"
                      type="submit"
                      disabled={
                        isSubmitting ||
                        !watchSessionId ||
                        monthRanges.length === 0
                      }
                    >
                      {isSubmitting ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default InstallmentPlan;
